import { useState, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/common/ui/card'
import { Button } from '@/common/ui/button'
import { Textarea } from '@/common/ui/textarea'
import { Check } from 'lucide-react'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import useMutationUploadCustomCss from '@/core/api/hooks/useMutationUploadCustomCss'
import useQueryCssContent from '@/common/hooks/useQueryCssContent'

interface CssUploadBoxProps {
	projectName: string
	currentCssUrl?: string
	onCssUploaded?: (cssUrl: string) => void
}

export default function CssUploadBox({
	projectName,
	currentCssUrl,
	onCssUploaded,
}: CssUploadBoxProps) {
	// Generate default CSS URL
	const defaultCssUrl = `https://image.panda-storage.com/${projectName}/config/index.css`
	const cssUrlToFetch = currentCssUrl || defaultCssUrl

	const queryClient = useQueryClient()
	const {
		data: fetchedCssContent,
		isLoading: isLoadingCss,
		isError,
	} = useQueryCssContent(cssUrlToFetch)

	const [cssContent, setCssContent] = useState<string>('')
	const [isUploading, setIsUploading] = useState(false)

	const { mutateAsync: uploadCustomCss } = useMutationUploadCustomCss()

	// Sync fetched content to local state for editing
	useEffect(() => {
		if (fetchedCssContent) {
			setCssContent(fetchedCssContent)
		}
	}, [fetchedCssContent])

	// Handle error state
	useEffect(() => {
		if (isError) {
			showErrorToast('ไม่สามารถโหลดไฟล์ CSS ปัจจุบันได้')
			setCssContent('')
		}
	}, [isError])

	const handleApply = async () => {
		if (!cssContent.trim()) {
			showErrorToast('กรุณาใส่เนื้อหา CSS')
			return
		}

		try {
			setIsUploading(true)

			// Upload CSS content directly to backend
			const result = await uploadCustomCss(cssContent)

			// Invalidate query to refetch with new ETag
			await queryClient.invalidateQueries({
				queryKey: ['cssContent', cssUrlToFetch],
			})

			// Call callback if provided
			if (onCssUploaded) {
				onCssUploaded(result.fileUrl)
			}

			toast.success('ไฟล์ CSS ถูกอัปโหลดเรียบร้อยแล้ว')
		} catch (error) {
			console.error('CSS upload failed:', error)
			showErrorToast('ไม่สามารถอัปโหลดไฟล์ CSS ได้ กรุณาลองใหม่อีกครั้ง')
		} finally {
			setIsUploading(false)
		}
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle>ตั้งค่าตีมสีสำหรับเว็บไซต์</CardTitle>
				<CardDescription>แก้ไข CSS สำหรับ {projectName}</CardDescription>
			</CardHeader>
			<CardContent className="space-y-4">
				{/* CSS Textarea */}
				<div className="space-y-2">
					<Textarea
						value={cssContent}
						onChange={(e) => setCssContent(e.target.value)}
						placeholder="วางโค้ด CSS ที่นี่..."
						className="min-h-[300px] font-mono text-sm"
						disabled={isLoadingCss || isUploading}
					/>
					{isLoadingCss && (
						<p className="text-sm text-muted-foreground">
							กำลังโหลด CSS ปัจจุบัน...
						</p>
					)}
				</div>

				{/* Action button */}
				<Button
					type="button"
					variant="default"
					size="sm"
					className="w-full"
					onClick={handleApply}
					disabled={isUploading || isLoadingCss || !cssContent.trim()}
				>
					<Check className="mr-2 h-4 w-4" />
					{isUploading ? 'กำลังอัปโหลด...' : 'ยืนยันการอัปโหลด'}
				</Button>

				{/* Help text */}
				<p className="text-sm text-muted-foreground">
					สามารถวางโค้ด CSS ที่นี่ได้โดยตรง
					โดยสามารถลองปรับและคัดลอกการตั้งค่าได้จากเว็บ demo ของเรา
				</p>
			</CardContent>
		</Card>
	)
}
